import { createSlice } from "@reduxjs/toolkit";
import { fetchOrderSummaryStatistics, fetchOrderList, fetchDailyOrderTrend } from "./thunk";

interface OrderSummaryState {
  // Statistics data
  statistics: {
    total_orders: number;
    from_quotation: number;
    direct_orders: number;
    confirmed: number;
    processing: number;
    completed: number;
    cancelled: number;
    from_quotation_completed: number;
    from_quotation_cancelled: number;
    from_quotation_processing: number;
    direct_completed: number;
    direct_cancelled: number;
    direct_processing: number;
  };
  statisticsLoading: boolean;
  statisticsError: string | null;

  // List data
  orderList: any[];
  totalRecords: number;
  listLoading: boolean;
  listError: string | null;

  // Daily order trend data
  dailyOrderTrend: Array<{ day: string; orders: number }>;
  dailyOrderTrendLoading: boolean;
  dailyOrderTrendError: string | null;
}

const initialState: OrderSummaryState = {
  statistics: {
    total_orders: 0,
    from_quotation: 0,
    direct_orders: 0,
    confirmed: 0,
    processing: 0,
    completed: 0,
    cancelled: 0,
    from_quotation_completed: 0,
    from_quotation_cancelled: 0,
    from_quotation_processing: 0,
    direct_completed: 0,
    direct_cancelled: 0,
    direct_processing: 0,
  },
  statisticsLoading: false,
  statisticsError: null,
  orderList: [],
  totalRecords: 0,
  listLoading: false,
  listError: null,
  dailyOrderTrend: [],
  dailyOrderTrendLoading: false,
  dailyOrderTrendError: null,
};

const orderSummarySlice = createSlice({
  name: "orderSummary",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Statistics thunks
      .addCase(fetchOrderSummaryStatistics.pending, (state) => {
        state.statisticsLoading = true;
        state.statisticsError = null;
      })
      .addCase(fetchOrderSummaryStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchOrderSummaryStatistics.rejected, (state, action) => {
        state.statisticsLoading = false;
        state.statisticsError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch order summary statistics";
        // Set default values on error
        state.statistics = {
          total_orders: 0,
          from_quotation: 0,
          direct_orders: 0,
          confirmed: 0,
          processing: 0,
          completed: 0,
          cancelled: 0,
          from_quotation_completed: 0,
          from_quotation_cancelled: 0,
          from_quotation_processing: 0,
          direct_completed: 0,
          direct_cancelled: 0,
          direct_processing: 0,
        };
      })
      // List thunks
      .addCase(fetchOrderList.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchOrderList.fulfilled, (state, action) => {
        state.listLoading = false;
        state.orderList = action.payload.data;
        state.totalRecords = action.payload.totalRecords;
      })
      .addCase(fetchOrderList.rejected, (state, action) => {
        state.listLoading = false;
        state.listError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch order list";
        state.orderList = [];
        state.totalRecords = 0;
      })
      // Daily order trend thunks
      .addCase(fetchDailyOrderTrend.pending, (state) => {
        state.dailyOrderTrendLoading = true;
        state.dailyOrderTrendError = null;
      })
      .addCase(fetchDailyOrderTrend.fulfilled, (state, action) => {
        state.dailyOrderTrendLoading = false;
        state.dailyOrderTrend = action.payload;
      })
      .addCase(fetchDailyOrderTrend.rejected, (state, action) => {
        state.dailyOrderTrendLoading = false;
        state.dailyOrderTrendError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch daily order trend";
        state.dailyOrderTrend = [];
      });
  },
});

export default orderSummarySlice.reducer;

